/**
 * Spec v3.0 §7 — expiry, disable, delete, purge (pure, no I/O).
 * Keep in sync with backend/src/domain/pma/pmaLifecyclePolicy.js.
 */

export const PO_SCHEDULES = ['regular', 'weekend'];
export const PO_REGULAR_MONTHS = 9;
export const PO_WEEKEND_MONTHS = 15;
export const PO_AUTO_EXTENSION_MONTHS = 2;
export const PO_MAX_EXTENSIONS = 3;
export const PO_DISABLE_HOLD_DAYS = 45;
export const CORPORATE_DISABLE_DAYS = 45;
export const CORPORATE_DELETE_MONTHS = 2;
export const SHORT_PROGRAM_DAYS = 15;
export const SHORT_PROGRAM_DELETE_DAYS = 14;
export const BACKUP_PURGE_DAYS = 15;
export const DURATION_EDITABLE_PROGRAMS = ['TI', 'WS', 'UP', 'TS', 'CG'];

export function addMonths(date, months) {
  const copy = new Date(date.getTime());
  copy.setMonth(copy.getMonth() + months);
  return copy;
}

export function addDays(date, days) {
  const copy = new Date(date.getTime());
  copy.setDate(copy.getDate() + days);
  return copy;
}

export function formatPmaSheetDate(date = new Date()) {
  const d = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

export function parsePmaSheetDate(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (value == null || String(value).trim() === '') return null;
  const raw = String(value).trim();
  const dmy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]);
    let year = Number(dmy[3]);
    if (year < 100) year += 2000;
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function parseDurationToken(duration) {
  if (!duration) return {};
  const trimmed = String(duration).trim().toLowerCase();
  if (trimmed.endsWith('m')) {
    const value = Number(trimmed.slice(0, -1));
    return Number.isFinite(value) && value > 0 ? { months: value } : {};
  }
  if (trimmed.endsWith('d')) {
    const value = Number(trimmed.slice(0, -1));
    return Number.isFinite(value) && value > 0 ? { days: value } : {};
  }
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) && numeric > 0 ? { days: numeric } : {};
}

export function normalizeProgramDuration(value) {
  const parsed = parseDurationToken(value);
  if (parsed.months) return `${parsed.months}m`;
  if (parsed.days) return `${parsed.days}d`;
  return '';
}

export function normalizePoSchedule(value) {
  const raw = String(value || '')
    .trim()
    .toLowerCase();
  return raw === 'weekend' || raw === 'weekends' ? 'weekend' : 'regular';
}

export function inferLifecycleCategory(programType, userCategory, userType) {
  const prog = String(programType || '')
    .trim()
    .toUpperCase();
  if (prog === 'VE') return 've_trainer';
  if (prog === 'SP') return 'special';
  if (prog === 'CO') return 'corporate';
  if (prog === 'CG') return 'cg';

  const category = String(userCategory || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (category === 've_trainer' || category === 've') return 've_trainer';
  if (category === 'special' || category === 'special_privileged' || category === 'privileged') {
    return 'special';
  }
  if (category === 'corporate') return 'corporate';
  if (category === 'cg' || category === 'chipgrad') return 'cg';
  if (category === 'bulk' || category === 'bulk_workshop') return 'bulk';

  const type = String(userType || '').toLowerCase();
  if (type.includes('trainer')) return 've_trainer';
  if (type.includes('special') || type.includes('privileged')) return 'special';
  if (type.includes('corporate')) return 'corporate';
  if (type.includes('chipgrad')) return 'cg';
  return category || 'normal';
}

export function isLifecycleExempt(userCategory, programType, userType) {
  const category = inferLifecycleCategory(programType, userCategory, userType);
  const prog = String(programType || '')
    .trim()
    .toUpperCase();
  return category === 've_trainer' || category === 'special' || prog === 'VE' || prog === 'SP';
}

function isCorporateLifecycle(userCategory, programType, userType) {
  const category = inferLifecycleCategory(programType, userCategory, userType);
  return category === 'corporate' || String(programType || '').toUpperCase() === 'CO';
}

export function defaultDurationToken({
  programType,
  userCategory,
  userType,
  poSchedule,
  programDuration,
  duration,
} = {}) {
  if (isLifecycleExempt(userCategory, programType, userType)) return '';
  const prog = String(programType || '').toUpperCase();
  if (DURATION_EDITABLE_PROGRAMS.includes(prog)) {
    const override = normalizeProgramDuration(programDuration || duration);
    if (override) return override;
  }
  if (isCorporateLifecycle(userCategory, programType, userType)) return `${CORPORATE_DISABLE_DAYS}d`;
  if (String(programType || '').toUpperCase() === 'PO') {
    return normalizePoSchedule(poSchedule) === 'weekend' ? `${PO_WEEKEND_MONTHS}m` : `${PO_REGULAR_MONTHS}m`;
  }
  if (String(programType || '').toUpperCase() === 'WS') return '15d';
  return `${PO_REGULAR_MONTHS}m`;
}

function durationApproxDays(token) {
  const parsed = parseDurationToken(token);
  if (parsed.months) return parsed.months * 30;
  if (parsed.days) return parsed.days;
  return 0;
}

export function resolveDeleteAndPurge({
  programType,
  userCategory,
  userType,
  durationToken,
  expiryDate,
} = {}) {
  if (!expiryDate || isLifecycleExempt(userCategory, programType, userType)) {
    return { deleteDate: null, purgeDate: null };
  }
  let deleteDate;
  if (isCorporateLifecycle(userCategory, programType, userType)) {
    deleteDate = addMonths(expiryDate, CORPORATE_DELETE_MONTHS);
  } else if (String(programType || '').toUpperCase() === 'PO') {
    deleteDate = addDays(expiryDate, PO_DISABLE_HOLD_DAYS);
  } else if (durationApproxDays(durationToken) < SHORT_PROGRAM_DAYS) {
    deleteDate = addDays(expiryDate, SHORT_PROGRAM_DELETE_DAYS);
  } else {
    deleteDate = addMonths(expiryDate, 1);
  }
  return { deleteDate, purgeDate: addDays(deleteDate, BACKUP_PURGE_DAYS) };
}

export function resolvePmaLifecycle(policy = {}, { startDate, expiryDate } = {}) {
  const start = startDate instanceof Date && !Number.isNaN(startDate.getTime()) ? startDate : new Date();
  const programType = String(policy.programType || policy.program_type || '')
    .trim()
    .toUpperCase();
  const userCategory = inferLifecycleCategory(
    programType,
    policy.userCategory || policy.user_category,
    policy.userType || policy.usertype || policy.programName
  );
  const poSchedule = normalizePoSchedule(policy.poSchedule || policy.po_schedule);

  if (isLifecycleExempt(userCategory, programType, policy.userType)) {
    return {
      exempt: true,
      duration: '',
      poSchedule: null,
      startDate: start,
      expiryDate: null,
      deleteDate: null,
      purgeDate: null,
      maxExtensions: 0,
      reason: 'VE Trainer and Special Privileged have no expiry, disable, or delete',
    };
  }

  const duration =
    normalizeProgramDuration(policy.duration) ||
    defaultDurationToken({
      programType,
      userCategory,
      userType: policy.userType,
      poSchedule,
      programDuration: policy.programDuration,
    });
  const parsed = parseDurationToken(duration);
  const computedExpiry = parsed.months
    ? addMonths(start, parsed.months)
    : addDays(start, parsed.days || CORPORATE_DISABLE_DAYS);
  const explicitExpiry =
    expiryDate instanceof Date && !Number.isNaN(expiryDate.getTime()) ? expiryDate : parsePmaSheetDate(expiryDate);
  const resolvedExpiry = explicitExpiry || computedExpiry;
  const { deleteDate, purgeDate } = resolveDeleteAndPurge({
    programType,
    userCategory,
    durationToken: duration,
    expiryDate: resolvedExpiry,
  });

  return {
    exempt: false,
    duration,
    poSchedule: programType === 'PO' ? poSchedule : null,
    startDate: start,
    expiryDate: resolvedExpiry,
    deleteDate,
    purgeDate,
    maxExtensions: programType === 'PO' ? PO_MAX_EXTENSIONS : 0,
    reason: isCorporateLifecycle(userCategory, programType)
      ? 'Corporate: disable after 45 days, delete after +2 months'
      : programType === 'PO'
        ? poSchedule === 'weekend'
          ? 'PO Weekend: 15 months, then disable + 45 days'
          : 'PO Regular: 9 months, then disable + 45 days'
        : durationApproxDays(duration) < SHORT_PROGRAM_DAYS
          ? 'Short program: disable on expiry, delete after 2 weeks'
          : 'Program ≥ 15 days: disable on expiry, delete after 1 month',
  };
}
